import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useUserCategories } from '../../hooks/useUserCategories';

interface SubjectFilterBarProps {
  subjects: string[];
  selectedSubject: string;
  onSelectSubject: (subject: string) => void;
  showCount?: boolean;
  subjectCounts?: Record<string, number>;
  onRefresh?: () => void;
}

export default function SubjectFilterBar({
  subjects,
  selectedSubject,
  onSelectSubject,
  showCount = false,
  subjectCounts = {},
  onRefresh,
}: SubjectFilterBarProps) {
  const { user } = useAuth();
  const {
    allCategories,
    searchQuery,
    setSearchQuery,
    addCategory,
    toggleFavorite,
  } = useUserCategories('subject');

  const [showAddForm, setShowAddForm] = useState(false);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [addError, setAddError] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // ユーザーが切り替わったら検索クエリ・フォームをリセット
  useEffect(() => {
    setSearchQuery('');
    setShowAddForm(false);
    setNewSubjectName('');
    setAddError('');
  }, [user?.id]);

  // お気に入り情報をマップ化
  const favoriteMap = new Map(
    allCategories.map((cat) => [cat.category_name, { id: cat.id, isFavorite: cat.is_favorite }])
  );

  // 検索フィルタリング
  const filteredSubjects = searchQuery
    ? subjects.filter((s) => s.toLowerCase().includes(searchQuery.toLowerCase()))
    : subjects;

  // お気に入り順にソート
  const sortedSubjects = [...filteredSubjects].sort((a, b) => {
    const aFav = favoriteMap.get(a)?.isFavorite || false;
    const bFav = favoriteMap.get(b)?.isFavorite || false;
    if (aFav && !bFav) return -1;
    if (!aFav && bFav) return 1;
    return 0;
  });

  const handleAddSubject = async () => {
    if (!user) return;
    if (!newSubjectName.trim()) {
      setAddError('科目名を入力してください');
      return;
    }
    if (subjects.includes(newSubjectName.trim())) {
      setAddError('この科目は既に存在します');
      return;
    }

    setIsAdding(true);
    setAddError('');
    try {
      await addCategory(newSubjectName.trim());
      setNewSubjectName('');
      setShowAddForm(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      setAddError('科目の追加に失敗しました');
    } finally {
      setIsAdding(false);
    }
  };

  const handleToggleFavorite = async (subjectName: string) => {
    if (!user) return;
    const info = favoriteMap.get(subjectName);
    if (!info) return;
    try {
      await toggleFavorite(info.id, info.isFavorite);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('お気に入り更新エラー:', err);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">
          <i className="ri-folder-line mr-1.5 text-teal-600 dark:text-teal-400"></i>科目で絞り込み
        </label>
        <div className="flex items-center gap-2 flex-1 max-w-md">
          {user && (
            <>
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 text-xs"></i>
                <input
                  type="text"
                  placeholder="科目を検索..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 dark:text-white"
                />
              </div>
              {!showAddForm && (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="px-3 py-1.5 bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-lg text-xs font-medium hover:bg-teal-100 dark:hover:bg-teal-900/50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                  title="科目を追加"
                >
                  <i className="ri-add-line text-sm"></i>
                  追加
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* 追加フォーム */}
      {showAddForm && user && (
        <div className="mb-3 p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl">
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <input
                type="text"
                placeholder="新しい科目名を入力..."
                value={newSubjectName}
                onChange={(e) => {
                  setNewSubjectName(e.target.value);
                  setAddError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddSubject();
                  if (e.key === 'Escape') {
                    setShowAddForm(false);
                    setNewSubjectName('');
                    setAddError('');
                  }
                }}
                className="w-full px-3 py-2 border border-teal-300 dark:border-teal-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-800 dark:text-white"
                autoFocus
              />
              {addError && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{addError}</p>
              )}
            </div>
            <button
              onClick={handleAddSubject}
              disabled={isAdding || !newSubjectName.trim()}
              className="px-4 py-2 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:from-teal-600 hover:to-emerald-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAdding ? '追加中...' : '追加'}
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewSubjectName('');
                setAddError('');
              }}
              className="px-3 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-sm transition-colors cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
      )}

      {/* 科目タグ */}
      <div className="flex flex-wrap gap-1.5 sm:gap-2">
        <button
          onClick={() => onSelectSubject('')}
          className={`px-3 py-2 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap min-h-[36px] ${
            selectedSubject === ''
              ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-sm'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
          }`}
        >
          すべて
          {showCount && subjectCounts && (
            <span className="ml-1 opacity-70">
              ({Object.values(subjectCounts).reduce((a, b) => a + b, 0)})
            </span>
          )}
        </button>

        {sortedSubjects.map((subject) => {
          const info = favoriteMap.get(subject);
          const isFavorite = info?.isFavorite || false;
          const count = showCount && subjectCounts ? subjectCounts[subject] || 0 : 0;

          return (
            <div key={subject} className="relative group">
              <button
                onClick={() => onSelectSubject(subject)}
                className={`px-3 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap min-h-[36px] flex items-center gap-1.5 ${
                  selectedSubject === subject
                    ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-sm'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                } ${user ? 'pr-8' : ''}`}
              >
                {isFavorite && (
                  <i className="ri-star-fill text-amber-400 text-xs"></i>
                )}
                {subject}
                {showCount && count > 0 && (
                  <span className="opacity-70">({count})</span>
                )}
              </button>

              {/* お気に入りボタン（ログインユーザーのみ） */}
              {user && info && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleFavorite(subject);
                  }}
                  className={`absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full transition-all ${
                    isFavorite
                      ? 'opacity-100'
                      : 'opacity-0 group-hover:opacity-100'
                  } ${
                    selectedSubject === subject
                      ? 'hover:bg-white/20'
                      : 'hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                  title={isFavorite ? 'お気に入り解除' : 'お気に入り登録'}
                >
                  <i
                    className={`${
                      isFavorite ? 'ri-star-fill text-amber-400' : 'ri-star-line'
                    } text-xs ${
                      selectedSubject === subject
                        ? 'text-white'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  ></i>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {searchQuery && filteredSubjects.length === 0 && (
        <div className="mt-3 text-center py-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            「{searchQuery}」に一致する科目が見つかりません
          </p>
        </div>
      )}
    </div>
  );
}
